import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL } from '../constants';
import {
  UserFilters,
  UsersResponse,
  UserDetailResponse,
  AdminPlayerFilterMetaResponse,
  BanUserRequest,
  BanUserResponse,
  UnbanUserResponse,
  AgentTerminatePreviewResponse,
  TerminatePlayerPreview,
  TerminatePlayerRequest,
  TerminatePlayerResponse,
  CloseAndReleasePlayerRequest,
  CloseAndReleasePlayerResponse,
  UpdateUserStatusRequest,
  UpdateUserStatusResponse,
  TransactionFilters,
  TransactionsResponse,
  TransactionDetailResponse,
  TransactionStatsResponse,
  UpdateTransactionStatusRequest,
  UpdateTransactionStatusResponse,
  WalletResponse,
  WalletTransactionsResponse,
  WalletTransactionFilters,
  WithdrawalFilters,
  WithdrawalsResponse,
  ApproveWithdrawalRequest,
  ApproveWithdrawalResponse,
  RejectWithdrawalRequest,
  RejectWithdrawalResponse,
  CompleteWithdrawalRequest,
  CompleteWithdrawalResponse,
  WithdrawalPayoutLogResponse,
  TicketFilters,
  TicketsResponse,
  TicketResponse,
  ReplyTicketRequest,
  ReplyTicketResponse,
  UpdateTicketStatusRequest,
  UpdateTicketStatusResponse,
  MarkTicketDoneResponse,
  SupportStatsResponse,
  SupportCategory,
  AdminListFilters,
  AdminListResponse,
  AdminDetailResponse,
  CreateAdminRequest,
  UpdateAdminRequest,
  ResetAdminPasswordRequest,
  UpdateMySettingsRequest,
  UpdateMySettingsResponse,
  RequestEmailChangeRequest,
  RequestEmailChangeResponse,
  VerifyEmailChangeRequest,
  VerifyEmailChangeResponse,
  PermissionsListResponse,
  AdminPermissionsResponse,
  GrantPermissionsRequest,
  GrantPermissionsResponse,
  RevokePermissionsRequest,
  RevokePermissionsResponse,
  SetPermissionsRequest,
  SetPermissionsResponse,
  GameWalletFilters,
  GameWalletListResponse,
  GameWalletDetailResponse,
  GameWalletTransactionFilters,
  GameWalletTransactionsResponse,
  ProviderTransactionFilters,
  ProviderHealthResponse,
  ProviderSyncLogsFilters,
  ProviderSyncLogsResponse,
  SyncWalletResponse,
  GameWalletStatisticsResponse,
  GameProvidersResponse,
  GameProviderDetailResponse,
  CreateGameProviderRequest,
  CreateGameProviderResponse,
  UpdateGameProviderRequest,
  UpdateGameProviderResponse,
  ReorderGameProvidersResponse,
  DeleteGameProviderResponse,
  AgentBalancesFilters,
  AgentBalancesResponse,
  AgentBalancesSyncResponse,
  AgentBalanceHealthSnapshotsResponse,
  FullTransactionDetailResponse,
  RetrySyncRequest,
  RetrySyncResponse,
  FailedTransactionsFilters,
  FailedTransactionsResponse,
  LoginCredentialsRequest,
  LoginCredentialsResponse,
  LoginResponse,
  VerifyOtpRequest,
  ForgotPasswordRequestResponse,
  RefundTransactionRequest,
  RefundTransactionResponse,
  RefundAllTransactionsRequest,
  RefundAllTransactionsResponse,
  VfxConfigurationResponse,
  UpdateVfxConfigRequest,
  PresetBackgroundsResponse,
  VfxScheduleListResponse,
  VfxScheduleDetailResponse,
  CreateVfxScheduleRequest,
  UpdateVfxScheduleRequest,
  TransactionLimitsConfigResponse,
  UpdateTransactionLimitsConfigRequest,
  MessagingPlatformSettings,
  MessagingPlatformSettingsResponse,
  AdminShopMessagingPatchResponse,
  FraudFlagFilters,
  FraudFlagsResponse,
  ResolveFraudFlagRequest,
  ResolveFraudFlagResponse,
  SignupBonusGrantsFilters,
  SignupBonusGrantsResponse,
  SignupBonusKpiResponse,
  ReservedBalanceDetailResponse,
  ManualGameDepositFilters,
  ManualGameDepositsResponse,
  ManualGameDepositDetailResponse,
  ConfirmManualDepositRequest,
  ConfirmManualDepositResponse,
  RejectManualDepositRequest,
  RejectManualDepositResponse,
  UpdateGameAccountRequest,
  UpdateGameAccountResponse,
  ProfitAnalysisResponse,
  AdjustBalanceRequest,
  AdjustBalanceResponse,
  PlayerAutoRefundSummaryResponse,
  ClearAutoRefundWarningRequest,
  ClearAutoRefundWarningResponse,
  DomainsResponse,
  DomainResponse,
  DomainGameMappingsResponse,
  CreateDomainRequest,
  UpdateDomainRequest,
  KioskHealthResponse,
  DomainAnalyticsResponse,
  AgentsListResponse,
  CreateCdnAgentRequest,
  CreateCdnAgentResponse,
  CdnAgent,
  AdminAgentDetailResponse,
  CreateCdnShopRequest,
  CreateCdnShopResponse,
  AdminShopDetailResponse,
  AdminShopCreditAdjustRequest,
  AdminShopCreditAdjustResponse,
  AdminFinanceCorrectionRequest,
  AdminFinanceCorrectionResponse,
  CdnShop,
  AdminAgentCashFundingRequest,
  AdminAgentCashFundingResponse,
  AdminAgentPaymentTransactionsResponse,
  AdminAgentWalletTransactionsResponse,
  CostReportResponse,
  AdminBroadcastsListResponse,
  AdminBroadcastShopsMetaResponse,
  AdminNotificationApiRow,
  CreateAdminBroadcastRequest,
  SuperAgentsListResponse,
  CreateSuperAgentRequest,
  CreateSuperAgentResponse,
  SuperAgentDetailResponse,
  SuperAgentWalletTransactionsResponse,
  ProviderFeeConfigsResponse,
  UpsertProviderFeeConfigsRequest,
  AdminCashiersListResponse,
  AdminCreateCashierRequest,
  AdminCreateCashierResponse,
  AdminCashierReportResponse,
  AdminCashierShiftHistoryResponse,
  AdminFraudIndicatorsResponse,
  UsdWalletSummaryResponse,
  UsdWalletLedgerResponse,
  UsdWalletLedgerEntryResponse,
  CreditWalletSummaryResponse,
  CreditWalletLedgerResponse,
} from '../types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to attach auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          // Debug: log token info (first 20 chars only for security)
          console.log('=== REQUEST INTERCEPTOR ===');
          console.log('URL:', config.url);
          console.log('Token exists:', !!token);
          console.log('Token preview:', token.substring(0, 20) + '...');
          console.log('Authorization header:', config.headers.Authorization?.substring(0, 30) + '...');
        } else {
          console.warn('=== NO TOKEN FOUND ===');
          console.warn('URL:', config.url);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn('=== API 401 UNAUTHORIZED ===', error.config?.url);
          window.dispatchEvent(new Event('auth:unauthorized'));
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth methods (Admin Login V2)
  async loginCredentials(data: LoginCredentialsRequest): Promise<LoginCredentialsResponse> {
    const { usernameOrEmail, password, captchaToken } = data;
    try {
      const response = await this.client.post<LoginCredentialsResponse>('/admin-auth/login', {
        usernameOrEmail,
        password,
        ...(captchaToken ? { captchaToken } : {}),
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async verifyOtp(data: VerifyOtpRequest): Promise<LoginResponse> {
    const { pendingToken, otp } = data;
    try {
      const response = await this.client.post<LoginResponse>('/admin-auth/verify-otp', {
        pendingToken,
        otp,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async requestForgotPasswordOtp(contact: string): Promise<ForgotPasswordRequestResponse> {
    try {
      const response = await this.client.post<ForgotPasswordRequestResponse>(
        '/admin-auth/forgot-password/request-otp',
        { contact }
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async verifyForgotPasswordOtp(
    token: string,
    otp: string
  ): Promise<{ success: boolean; message?: string; data?: { token: string } }> {
    try {
      const response = await this.client.post('/admin-auth/forgot-password/verify-otp', { token, otp });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async confirmForgotPasswordReset(payload: {
    token: string;
    otp: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.client.post('/admin-auth/forgot-password/confirm', payload);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  /** @deprecated Legacy flow - use loginCredentials + verifyOtp */
  async sendEmailCode(email: string, password: string) {
    const response = await this.client.post('/admin-auth/login', { email, password });
    return response.data;
  }

  /** @deprecated Legacy flow - use verifyOtp with pendingToken */
  async login(email: string, otp: string) {
    const response = await this.client.post('/admin-auth/verify', { email, otp });
    return response.data;
  }

  // Dashboard methods
  private async getDashboardTab<T>(
    tab: string,
    params: { period: string; tz?: string; dateFrom?: string; dateTo?: string },
    refresh = false,
  ): Promise<T> {
    const response = await this.client.get(`/admin/dashboard/${tab}`, {
      params: {
        period: params.period,
        ...(params.tz ? { tz: params.tz } : {}),
        ...(params.period === 'custom' && params.dateFrom ? { date_from: params.dateFrom } : {}),
        ...(params.period === 'custom' && params.dateTo ? { date_to: params.dateTo } : {}),
        ...(refresh ? { refresh: '1' } : {}),
      },
    });
    return response.data?.data || response.data;
  }

  async getDashboardExecutive(
    params: { period: string; tz?: string; dateFrom?: string; dateTo?: string },
    refresh = false,
  ) {
    return this.getDashboardTab<import('../types').DashboardExecutiveData>(
      'executive',
      params,
      refresh,
    );
  }

  async getDashboardOverview(
    params: { period: string; tz?: string; dateFrom?: string; dateTo?: string },
    refresh = false,
  ) {
    return this.getDashboardTab<import('../types').DashboardOverviewData>('overview', params, refresh);
  }

  async getDashboardPlatformPlayers(
    params: { period: string; tz?: string; dateFrom?: string; dateTo?: string },
    refresh = false,
  ) {
    return this.getDashboardTab<import('../types').DashboardPlatformPlayersData>(
      'platform-players',
      params,
      refresh,
    );
  }

  async getDashboardShopPlayers(
    params: { period: string; tz?: string; dateFrom?: string; dateTo?: string },
    refresh = false,
  ) {
    return this.getDashboardTab<import('../types').DashboardShopPlayersData>(
      'shop-players',
      params,
      refresh,
    );
  }

  async getDashboardAgentNetwork(
    params: { period: string; tz?: string; dateFrom?: string; dateTo?: string },
    refresh = false,
  ) {
    return this.getDashboardTab<import('../types').DashboardAgentNetworkData>(
      'agent-network',
      params,
      refresh,
    );
  }

  // User methods
  async getUsers(filters: UserFilters): Promise<UsersResponse> {
    console.log('=== API GET USERS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/users');
    console.log('Filters:', filters);

    try {
      const response = await this.client.get('/admin/users', { params: filters });
      console.log('=== API GET USERS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET USERS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  async getPlayerFilterMeta(): Promise<AdminPlayerFilterMetaResponse> {
    try {
      const response = await this.client.get<AdminPlayerFilterMetaResponse>(
        '/admin/users/meta/agent-shop-options'
      );
      return response.data;
    } catch (error: unknown) {
      return Promise.reject(error);
    }
  }

  async getUserDetails(userId: string): Promise<UserDetailResponse> {
    console.log('=== API GET USER DETAILS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/users/${userId}`);

    try {
      const response = await this.client.get(`/admin/users/${userId}`);
      console.log('=== API GET USER DETAILS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET USER DETAILS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  async banUser(userId: string, data: BanUserRequest): Promise<BanUserResponse> {
    console.log('=== API BAN USER CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/users/${userId}/ban`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/admin/users/${userId}/ban`, data);
      console.log('=== API BAN USER RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API BAN USER ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  async unbanUser(userId: string): Promise<UnbanUserResponse> {
    console.log('=== API UNBAN USER CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/users/${userId}/unban`);

    try {
      const response = await this.client.post(`/admin/users/${userId}/unban`);
      console.log('=== API UNBAN USER RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API UNBAN USER ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  async getTerminatePlayerPreview(userId: string): Promise<{ success: boolean; data: TerminatePlayerPreview }> {
    const response = await this.client.get(`/admin/users/${userId}/terminate/preview`);
    return response.data;
  }

  async terminatePlayer(userId: string, data: TerminatePlayerRequest): Promise<TerminatePlayerResponse> {
    const response = await this.client.post(`/admin/users/${userId}/terminate`, data);
    return response.data;
  }

  async closeAndReleasePlayer(userId: string, data: CloseAndReleasePlayerRequest): Promise<CloseAndReleasePlayerResponse> {
    const response = await this.client.post(`/admin/users/${userId}/close-and-release`, data);
    return response.data;
  }

  async updateUserStatus(userId: string, data: UpdateUserStatusRequest): Promise<UpdateUserStatusResponse> {
    console.log('=== API UPDATE USER STATUS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/users/${userId}/status`);
    console.log('Data:', data);

    try {
      const response = await this.client.patch(`/admin/users/${userId}/status`, data);
      console.log('=== API UPDATE USER STATUS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API UPDATE USER STATUS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  // Transaction methods
  async getTransactions(filters: TransactionFilters): Promise<TransactionsResponse> {
    console.log('=== API GET TRANSACTIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/transactions');
    console.log('Filters:', filters);

    try {
      const response = await this.client.get('/admin/transactions', { params: filters });
      console.log('=== API GET TRANSACTIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET TRANSACTIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  async getTransactionStats(filters?: Partial<TransactionFilters>): Promise<TransactionStatsResponse> {
    console.log('=== API GET TRANSACTION STATS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/transactions/stats');
    console.log('Filters:', filters);

    try {
      const response = await this.client.get('/admin/transactions/stats', { params: filters });
      console.log('=== API GET TRANSACTION STATS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET TRANSACTION STATS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  async getTransactionDetail(transactionId: number): Promise<TransactionDetailResponse> {
    try {
      const response = await this.client.get(`/admin/transactions/${transactionId}`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getTransactionAllocations(transactionId: number): Promise<import('../types').TransactionAllocationsResponse> {
    try {
      const response = await this.client.get(`/admin/transactions/${transactionId}/allocations`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async updateTransactionStatus(
    transactionId: number,
    data: UpdateTransactionStatusRequest
  ): Promise<UpdateTransactionStatusResponse> {
    console.log('=== API UPDATE TRANSACTION STATUS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/transactions/${transactionId}/status`);
    console.log('Data:', data);

    try {
      const response = await this.client.patch(`/admin/transactions/${transactionId}/status`, data);
      console.log('=== API UPDATE TRANSACTION STATUS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API UPDATE TRANSACTION STATUS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  // Wallet methods
  async getUserWallet(userId: string): Promise<WalletResponse> {
    console.log('=== API GET USER WALLET CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/users/${userId}/wallet`);

    try {
      const response = await this.client.get(`/admin/users/${userId}/wallet`);
      console.log('=== API GET USER WALLET RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET USER WALLET ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  async getWalletTransactions(
    userId: string,
    filters: WalletTransactionFilters
  ): Promise<WalletTransactionsResponse> {
    console.log('=== API GET WALLET TRANSACTIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/users/${userId}/wallet/transactions`);
    console.log('Filters:', filters);

    try {
      const response = await this.client.get(`/admin/users/${userId}/wallet/transactions`, {
        params: filters
      });
      console.log('=== API GET WALLET TRANSACTIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET WALLET TRANSACTIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      // Re-throw the error instead of returning mock data
      return Promise.reject(error);
    }
  }

  async getReservedBalanceDetail(userId: string): Promise<ReservedBalanceDetailResponse> {
    console.log('=== API GET RESERVED BALANCE DETAIL CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/users/${userId}/wallet/reserved-balance-detail`);

    try {
      const response = await this.client.get(`/admin/users/${userId}/wallet/reserved-balance-detail`);
      console.log('=== API GET RESERVED BALANCE DETAIL RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET RESERVED BALANCE DETAIL ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  // ===== WITHDRAWAL REQUEST METHODS =====

  async getWithdrawals(filters: WithdrawalFilters): Promise<WithdrawalsResponse> {
    console.log('=== API GET WITHDRAWALS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/payment/admin/withdrawals/pending');
    console.log('Filters:', filters);

    try {
      const response = await this.client.get('/payment/admin/withdrawals/pending', {
        params: filters
      });
      console.log('=== API GET WITHDRAWALS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET WITHDRAWALS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async approveWithdrawal(transactionId: string, data: ApproveWithdrawalRequest): Promise<ApproveWithdrawalResponse> {
    console.log('=== API APPROVE WITHDRAWAL CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/payment/admin/withdrawals/${transactionId}/approve`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/payment/admin/withdrawals/${transactionId}/approve`, data);
      console.log('=== API APPROVE WITHDRAWAL RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API APPROVE WITHDRAWAL ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async rejectWithdrawal(transactionId: string, data: RejectWithdrawalRequest): Promise<RejectWithdrawalResponse> {
    console.log('=== API REJECT WITHDRAWAL CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/payment/admin/withdrawals/${transactionId}/reject`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/payment/admin/withdrawals/${transactionId}/reject`, data);
      console.log('=== API REJECT WITHDRAWAL RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API REJECT WITHDRAWAL ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async completeWithdrawal(transactionId: string, data: CompleteWithdrawalRequest): Promise<CompleteWithdrawalResponse> {
    console.log('=== API COMPLETE WITHDRAWAL CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/payment/admin/withdrawals/${transactionId}/complete`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/payment/admin/withdrawals/${transactionId}/complete`, data);
      console.log('=== API COMPLETE WITHDRAWAL RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API COMPLETE WITHDRAWAL ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async completeGameWithdrawalRecovery(
    transactionId: string,
    data: CompleteWithdrawalRequest
  ): Promise<CompleteWithdrawalResponse> {
    try {
      const response = await this.client.post(
        `/payment/admin/withdrawals/${transactionId}/complete-recovery`,
        data
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }


  async getWithdrawalPayoutLog(transactionId: string): Promise<WithdrawalPayoutLogResponse> {
    console.log('=== API GET WITHDRAWAL PAYOUT LOG CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/payment/admin/withdrawals/${transactionId}/payout-log`);

    try {
      const response = await this.client.get(`/payment/admin/withdrawals/${transactionId}/payout-log`);
      console.log('=== API GET WITHDRAWAL PAYOUT LOG RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET WITHDRAWAL PAYOUT LOG ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async retryWithdrawalPayout(transactionId: string): Promise<{ success: boolean; message: string; statusCode: number; timestamp: string }> {
    console.log('=== API RETRY WITHDRAWAL PAYOUT CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/payment/admin/withdrawals/${transactionId}/retry-payout`);

    try {
      const response = await this.client.post(`/payment/admin/withdrawals/${transactionId}/retry-payout`);
      console.log('=== API RETRY WITHDRAWAL PAYOUT RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API RETRY WITHDRAWAL PAYOUT ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  // Support Ticket APIs
  async getTickets(filters?: TicketFilters): Promise<TicketsResponse> {
    console.log('=== API GET TICKETS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/support/admin/tickets');
    console.log('Filters:', filters);

    try {
      const response = await this.client.get('/support/admin/tickets', { params: filters });
      console.log('=== API GET TICKETS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET TICKETS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async searchTickets(params: {
    email?: string;
    phone?: string;
    cursor?: string;
    direction?: 'next' | 'prev';
    limit?: number;
    status?: string;
    category?: string;
    tier?: string;
  }): Promise<TicketsResponse> {
    const response = await this.client.get('/support/admin/tickets/search', { params });
    return response.data;
  }

  async getTicket(ticketNumber: string): Promise<TicketResponse> {
    console.log('=== API GET TICKET CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/support/tickets/${ticketNumber}`);

    try {
      const response = await this.client.get(`/support/tickets/${ticketNumber}`);
      console.log('=== API GET TICKET RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET TICKET ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async replyTicket(ticketNumber: string, data: ReplyTicketRequest): Promise<ReplyTicketResponse> {
    console.log('=== API REPLY TICKET CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/support/admin/tickets/${ticketNumber}/reply`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/support/admin/tickets/${ticketNumber}/reply`, data);
      console.log('=== API REPLY TICKET RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API REPLY TICKET ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async updateTicketStatus(ticketNumber: string, data: UpdateTicketStatusRequest): Promise<UpdateTicketStatusResponse> {
    console.log('=== API UPDATE TICKET STATUS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/support/admin/tickets/${ticketNumber}`);
    console.log('Data:', data);

    try {
      const response = await this.client.patch(`/support/admin/tickets/${ticketNumber}`, data);
      console.log('=== API UPDATE TICKET STATUS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API UPDATE TICKET STATUS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async markTicketDone(ticketNumber: string): Promise<MarkTicketDoneResponse> {
    console.log('=== API MARK TICKET DONE CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/support/admin/tickets/${ticketNumber}/done`);

    try {
      const response = await this.client.patch(`/support/admin/tickets/${ticketNumber}/done`);
      console.log('=== API MARK TICKET DONE RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API MARK TICKET DONE ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async getSupportStats(): Promise<SupportStatsResponse> {
    console.log('=== API GET SUPPORT STATS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/support/admin/stats');

    try {
      const response = await this.client.get('/support/admin/stats');
      console.log('=== API GET SUPPORT STATS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET SUPPORT STATS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  async getCategories(): Promise<{ success: boolean; data: SupportCategory[] }> {
    console.log('=== API GET CATEGORIES CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/support/categories');

    try {
      const response = await this.client.get('/support/categories');
      console.log('=== API GET CATEGORIES RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET CATEGORIES ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);

      return Promise.reject(error);
    }
  }

  // Admin management methods
  async getCurrentAdmin(): Promise<AdminDetailResponse> {
    console.log('=== API GET CURRENT ADMIN CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/manage/me');

    try {
      const response = await this.client.get('/admin/manage/me');
      console.log('=== API GET CURRENT ADMIN RESPONSE ===');
      console.log('Response:', response.data);
      
      // Parse permissions from API response
      if (response.data?.data?.permissions) {
        response.data.data.admin.permissionObjects = response.data.data.permissions;
        response.data.data.admin.permissions = response.data.data.permissions.map((p: any) => p.code);
      }
      
      return response.data;
    } catch (error: any) {
      console.log('=== API GET CURRENT ADMIN ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async updateMySettings(data: UpdateMySettingsRequest): Promise<UpdateMySettingsResponse> {
    try {
      const response = await this.client.patch<UpdateMySettingsResponse>('/admin/manage/me/settings', data);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async requestEmailChange(data: RequestEmailChangeRequest): Promise<RequestEmailChangeResponse> {
    try {
      const response = await this.client.post<RequestEmailChangeResponse>('/admin/manage/me/email/request', data);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async verifyEmailChange(data: VerifyEmailChangeRequest): Promise<VerifyEmailChangeResponse> {
    try {
      const response = await this.client.post<VerifyEmailChangeResponse>('/admin/manage/me/email/verify', data);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdmins(filters: AdminListFilters): Promise<AdminListResponse> {
    console.log('=== API GET ADMINS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/manage');
    console.log('Filters:', filters);

    const params = {
      ...filters,
      active: typeof filters.active === 'boolean' ? filters.active : undefined,
    };

    try {
      const response = await this.client.get('/admin/manage', { params });
      console.log('=== API GET ADMINS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET ADMINS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async createAdmin(data: CreateAdminRequest): Promise<AdminDetailResponse> {
    console.log('=== API CREATE ADMIN CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/manage');
    console.log('Data:', data);

    try {
      const response = await this.client.post('/admin/manage', data);
      console.log('=== API CREATE ADMIN RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API CREATE ADMIN ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async updateAdmin(adminId: string, data: UpdateAdminRequest): Promise<AdminDetailResponse> {
    console.log('=== API UPDATE ADMIN CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/manage/${adminId}`);
    console.log('Data:', data);

    try {
      const response = await this.client.patch(`/admin/manage/${adminId}`, data);
      console.log('=== API UPDATE ADMIN RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API UPDATE ADMIN ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async deleteAdmin(adminId: string): Promise<{ success: boolean; message: string }> {
    console.log('=== API DELETE ADMIN CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/manage/${adminId}`);

    try {
      const response = await this.client.delete(`/admin/manage/${adminId}`);
      console.log('=== API DELETE ADMIN RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API DELETE ADMIN ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async resetAdminPassword(adminId: string, data: ResetAdminPasswordRequest): Promise<{ success: boolean; message: string }> {
    console.log('=== API RESET ADMIN PASSWORD CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/manage/${adminId}/reset-password`);
    console.log('Data:', data);

    try {
      const response = await this.client.patch(`/admin/manage/${adminId}/reset-password`, data);
      console.log('=== API RESET ADMIN PASSWORD RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API RESET ADMIN PASSWORD ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  // Permission management methods
  async getAllPermissions(category?: string): Promise<PermissionsListResponse> {
    console.log('=== API GET ALL PERMISSIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/manage/permissions');
    console.log('Category:', category);

    try {
      const params = category ? { category } : {};
      const response = await this.client.get('/admin/manage/permissions', { params });
      console.log('=== API GET ALL PERMISSIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET ALL PERMISSIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getAdminPermissions(adminId: string): Promise<AdminPermissionsResponse> {
    console.log('=== API GET ADMIN PERMISSIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/manage/${adminId}/permissions`);

    try {
      const response = await this.client.get(`/admin/manage/${adminId}/permissions`);
      console.log('=== API GET ADMIN PERMISSIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET ADMIN PERMISSIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async grantPermissions(adminId: string, data: GrantPermissionsRequest): Promise<GrantPermissionsResponse> {
    console.log('=== API GRANT PERMISSIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/manage/${adminId}/permissions/grant`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/admin/manage/${adminId}/permissions/grant`, data);
      console.log('=== API GRANT PERMISSIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GRANT PERMISSIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async revokePermissions(adminId: string, data: RevokePermissionsRequest): Promise<RevokePermissionsResponse> {
    console.log('=== API REVOKE PERMISSIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/manage/${adminId}/permissions/revoke`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/admin/manage/${adminId}/permissions/revoke`, data);
      console.log('=== API REVOKE PERMISSIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API REVOKE PERMISSIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async setPermissions(adminId: string, data: SetPermissionsRequest): Promise<SetPermissionsResponse> {
    console.log('=== API SET PERMISSIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/manage/${adminId}/permissions`);
    console.log('Data:', data);

    try {
      const response = await this.client.put(`/admin/manage/${adminId}/permissions`, data);
      console.log('=== API SET PERMISSIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API SET PERMISSIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  // Game Wallet methods
  async getGameWallets(filters: GameWalletFilters): Promise<GameWalletListResponse> {
    console.log('=== API GET GAME WALLETS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/game-wallets');
    console.log('Filters:', filters);

    try {
      const response = await this.client.get('/admin/game-wallets', { params: filters });
      console.log('=== API GET GAME WALLETS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET GAME WALLETS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getGameWalletDetail(id: string): Promise<GameWalletDetailResponse> {
    console.log('=== API GET GAME WALLET DETAIL CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/game-wallets/${id}`);

    try {
      const response = await this.client.get(`/admin/game-wallets/${id}`);
      console.log('=== API GET GAME WALLET DETAIL RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET GAME WALLET DETAIL ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getGameWalletTransactions(
    id: string,
    filters: GameWalletTransactionFilters
  ): Promise<GameWalletTransactionsResponse> {
    console.log('=== API GET GAME WALLET TRANSACTIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/game-wallets/${id}/transactions`);
    console.log('Filters:', filters);

    try {
      const response = await this.client.get(`/admin/game-wallets/${id}/transactions`, {
        params: filters,
      });
      console.log('=== API GET GAME WALLET TRANSACTIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET GAME WALLET TRANSACTIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getProviderGameWalletTransactions(
    filters: ProviderTransactionFilters
  ): Promise<GameWalletTransactionsResponse> {
    const response = await this.client.get('/admin/game-wallets/transactions', { params: filters });
    return response.data;
  }

  async getProviderHealth(gameProviderId: string, hours = 24): Promise<ProviderHealthResponse> {
    const response = await this.client.get('/admin/game-wallets/provider-health', {
      params: { gameProviderId, hours },
    });
    return response.data;
  }

  async getProviderSyncLogs(filters: ProviderSyncLogsFilters): Promise<ProviderSyncLogsResponse> {
    const response = await this.client.get('/admin/game-wallets/provider-sync-logs', {
      params: filters,
    });
    return response.data;
  }

  async syncGameWallet(id: string): Promise<SyncWalletResponse> {
    console.log('=== API SYNC GAME WALLET CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/game-wallets/${id}/sync`);

    try {
      const response = await this.client.post(`/admin/game-wallets/${id}/sync`);
      console.log('=== API SYNC GAME WALLET RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API SYNC GAME WALLET ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getGameWalletStatistics(filters?: Partial<GameWalletFilters>): Promise<GameWalletStatisticsResponse> {
    console.log('=== API GET GAME WALLET STATISTICS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/game-wallets/statistics');
    console.log('Filters:', filters);

    try {
      const response = await this.client.get('/admin/game-wallets/statistics', { params: filters });
      console.log('=== API GET GAME WALLET STATISTICS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET GAME WALLET STATISTICS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  /** CSV for production game recall (provider_code, recall_mode, balances, game_username). */
  async previewGameRecallAll(params?: {
    gameProviderId?: string;
    search?: string;
  }): Promise<{
    totalCandidates: number;
    recallApiCount: number;
    estimatedRecallApiTotal: number;
    byMode: Record<string, number>;
  }> {
    const response = await this.client.get('/admin/game-wallets/recall-all/preview', { params });
    return response.data?.data ?? response.data;
  }

  async runGameRecallAll(body: {
    gameProviderId?: string;
    search?: string;
    dryRun?: boolean;
    limit?: number;
  }): Promise<{ jobId: string; dryRun: boolean }> {
    const response = await this.client.post('/admin/game-wallets/recall-all/run', body);
    const data = response.data?.data ?? response.data;
    return { jobId: data.jobId, dryRun: Boolean(data.dryRun) };
  }

  async getGameRecallJob(jobId: string): Promise<{
    id: string;
    status: string;
    dryRun?: boolean;
    total?: number;
    processed?: number;
    summary?: Record<string, number>;
    errors?: Array<{ providerCode?: string; userId?: string; error?: string }>;
    error?: string | null;
  }> {
    const response = await this.client.get(`/admin/game-wallets/recall-all/jobs/${jobId}`);
    return response.data?.data ?? response.data;
  }

  async exportGameRecallCsv(params?: {
    gameProviderId?: string;
    recallMode?: string;
    search?: string;
  }): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await this.client.get<Blob>('/admin/game-wallets/export-recall', {
        params,
        responseType: 'blob',
      });
      const disposition = String(response.headers['content-disposition'] || '');
      const match = /filename="([^"]+)"/i.exec(disposition);
      const filename = match?.[1] || `game-recall-export_${new Date().toISOString().slice(0, 10)}.csv`;
      return { blob: response.data, filename };
    } catch (error: any) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text) as { message?: string; error?: string };
          const msg = parsed.message || parsed.error || 'Export failed';
          return Promise.reject(new Error(typeof msg === 'string' ? msg : 'Export failed'));
        } catch {
          return Promise.reject(new Error('Export failed'));
        }
      }
      return Promise.reject(error);
    }
  }

  // Provider list for the Game Wallets monitoring page. Gated by walletManagement (not
  // gameProviderManagement) so finance/platform can view wallet providers without provider settings.
  async getGameWalletProviders(): Promise<GameProvidersResponse> {
    const response = await this.client.get('/admin/game-wallets/providers');
    return response.data;
  }

  async getGameProviders(): Promise<GameProvidersResponse> {
    console.log('=== API GET GAME PROVIDERS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/game-providers');

    try {
      const response = await this.client.get('/admin/game-providers');
      console.log('=== API GET GAME PROVIDERS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET GAME PROVIDERS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getGameProviderDetail(id: string): Promise<GameProviderDetailResponse> {
    console.log('=== API GET GAME PROVIDER DETAIL CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/game-providers/${id}`);

    try {
      const response = await this.client.get(`/admin/game-providers/${id}`);
      console.log('=== API GET GAME PROVIDER DETAIL RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET GAME PROVIDER DETAIL ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async createGameProvider(data: CreateGameProviderRequest): Promise<CreateGameProviderResponse> {
    console.log('=== API CREATE GAME PROVIDER CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/game-providers');
    console.log('Data:', data);

    try {
      const response = await this.client.post('/admin/game-providers', data);
      console.log('=== API CREATE GAME PROVIDER RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API CREATE GAME PROVIDER ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async updateGameProvider(id: string, data: UpdateGameProviderRequest): Promise<UpdateGameProviderResponse> {
    console.log('=== API UPDATE GAME PROVIDER CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/game-providers/${id}`);
    console.log('Data:', data);

    try {
      const response = await this.client.put(`/admin/game-providers/${id}`, data);
      console.log('=== API UPDATE GAME PROVIDER RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API UPDATE GAME PROVIDER ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async reorderGameProviders(orderedIds: string[]): Promise<ReorderGameProvidersResponse> {
    try {
      const response = await this.client.put('/admin/game-providers/display-order', { orderedIds });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async deleteGameProvider(id: string): Promise<DeleteGameProviderResponse> {
    console.log('=== API DELETE GAME PROVIDER CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/game-providers/${id}`);

    try {
      const response = await this.client.delete(`/admin/game-providers/${id}`);
      console.log('=== API DELETE GAME PROVIDER RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API DELETE GAME PROVIDER ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  // Agent Balances (by game) methods
  async getAgentBalances(filters?: AgentBalancesFilters): Promise<AgentBalancesResponse> {
    const response = await this.client.get('/admin/agent-balances', { params: filters });
    return response.data;
  }

  async syncAgentBalances(): Promise<AgentBalancesSyncResponse> {
    const response = await this.client.post('/admin/agent-balances/sync', {});
    return response.data;
  }

  async getAgentBalanceHealthSnapshots(hours = 24): Promise<AgentBalanceHealthSnapshotsResponse> {
    const response = await this.client.get('/admin/agent-balances/health-snapshots', {
      params: { hours },
    });
    return response.data;
  }

  async syncAgentBalanceOne(providerId: string): Promise<AgentBalancesSyncResponse> {
    const response = await this.client.post(`/admin/agent-balances/sync/${providerId}`, {});
    return response.data;
  }

  // Full Transaction & Retry Sync methods
  async getFullTransactionDetail(transactionId: string): Promise<FullTransactionDetailResponse> {
    console.log('=== API GET FULL TRANSACTION DETAIL CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/payment-transactions/${transactionId}`);

    try {
      const response = await this.client.get(`/admin/payment-transactions/${transactionId}`);
      console.log('=== API GET FULL TRANSACTION DETAIL RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET FULL TRANSACTION DETAIL ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async retrySyncTransaction(transactionId: string, data: RetrySyncRequest): Promise<RetrySyncResponse> {
    console.log('=== API RETRY SYNC TRANSACTION CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/payment-transactions/${transactionId}/sync-game-wallet`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/admin/payment-transactions/${transactionId}/sync-game-wallet`, data);
      console.log('=== API RETRY SYNC TRANSACTION RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API RETRY SYNC TRANSACTION ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getFailedTransactions(filters: FailedTransactionsFilters): Promise<FailedTransactionsResponse> {
    console.log('=== API GET FAILED TRANSACTIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/payment-transactions/failed-sync');
    console.log('Filters:', filters);

    try {
      const response = await this.client.get('/admin/payment-transactions/failed-sync', { params: filters });
      console.log('=== API GET FAILED TRANSACTIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET FAILED TRANSACTIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async ggusonepayManualCheck(
    paymentTransactionId: number,
  ): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> {
    const response = await this.client.post(
      `/payment/admin/payment-transactions/${paymentTransactionId}/ggusonepay/manual-check`,
    );
    return response.data;
  }

  async ggusonepayManualSettle(
    paymentTransactionId: number,
    data: { notes: string },
  ): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> {
    const response = await this.client.post(
      `/payment/admin/payment-transactions/${paymentTransactionId}/ggusonepay/manual-settle`,
      data,
    );
    return response.data;
  }

  async exportGgusonepayManualReviewLogs(params: {
    dateFrom?: string;
    dateTo?: string;
    type?: 'deposit' | 'withdrawal';
    fullyResolved?: 'true' | 'false';
    shopName?: string;
  }): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await this.client.get<Blob>(
        '/payment/admin/payment-transactions/ggusonepay/manual-review/export',
        {
          params,
          responseType: 'blob',
        },
      );
      const disposition = String(response.headers['content-disposition'] || '');
      const match = /filename="([^"]+)"/i.exec(disposition);
      const filename =
        match?.[1] ||
        `ggusonepay-manual-review_${params.dateFrom || 'start'}_to_${params.dateTo || 'end'}.csv`;
      return { blob: response.data, filename };
    } catch (error: unknown) {
      const err = error as { response?: { data?: Blob } };
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text) as { message?: string; error?: string };
          const msg = parsed.message || parsed.error || 'Export failed';
          return Promise.reject(new Error(typeof msg === 'string' ? msg : 'Export failed'));
        } catch {
          return Promise.reject(new Error('Export failed'));
        }
      }
      return Promise.reject(error);
    }
  }

  async refundTransaction(transactionId: number, data: RefundTransactionRequest): Promise<RefundTransactionResponse> {
    console.log('=== API REFUND TRANSACTION CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/payment/admin/payment-transactions/${transactionId}/refund`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/payment/admin/payment-transactions/${transactionId}/refund`, data);
      console.log('=== API REFUND TRANSACTION RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API REFUND TRANSACTION ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async refundAllTransactions(data: RefundAllTransactionsRequest): Promise<RefundAllTransactionsResponse> {
    console.log('=== API REFUND ALL TRANSACTIONS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/payment/admin/payment-transactions/refund-all');
    console.log('Data:', data);

    try {
      const response = await this.client.post('/payment/admin/payment-transactions/refund-all', data);
      console.log('=== API REFUND ALL TRANSACTIONS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API REFUND ALL TRANSACTIONS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  // ===== VFX CONFIGURATION METHODS =====

  async getVfxConfig(): Promise<VfxConfigurationResponse> {
    console.log('=== API GET VFX CONFIG CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/vfx-config');

    try {
      // Cache-bust so browser does not serve stale config (admin always sees latest)
      const response = await this.client.get('/admin/vfx-config', {
        params: { _t: Date.now() },
      });
      console.log('=== API GET VFX CONFIG RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET VFX CONFIG ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async updateVfxConfig(data: UpdateVfxConfigRequest): Promise<VfxConfigurationResponse> {
    console.log('=== API UPDATE VFX CONFIG CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/vfx-config');
    console.log('Data:', data);

    try {
      const response = await this.client.post('/admin/vfx-config', data);
      console.log('=== API UPDATE VFX CONFIG RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API UPDATE VFX CONFIG ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getPresetBackgrounds(): Promise<PresetBackgroundsResponse> {
    console.log('=== API GET PRESET BACKGROUNDS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/vfx-config/presets');

    try {
      const response = await this.client.get('/admin/vfx-config/presets');
      console.log('=== API GET PRESET BACKGROUNDS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET PRESET BACKGROUNDS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  // ===== VFX SCHEDULE METHODS =====

  async getVfxSchedules(): Promise<VfxScheduleListResponse> {
    console.log('=== API GET VFX SCHEDULES CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/vfx-config/schedules');

    try {
      const response = await this.client.get('/admin/vfx-config/schedules');
      console.log('=== API GET VFX SCHEDULES RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET VFX SCHEDULES ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getVfxSchedule(id: string): Promise<VfxScheduleDetailResponse> {
    console.log('=== API GET VFX SCHEDULE CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/vfx-config/schedules/${id}`);

    try {
      const response = await this.client.get(`/admin/vfx-config/schedules/${id}`);
      console.log('=== API GET VFX SCHEDULE RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET VFX SCHEDULE ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async createVfxSchedule(data: CreateVfxScheduleRequest): Promise<VfxScheduleDetailResponse> {
    console.log('=== API CREATE VFX SCHEDULE CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/vfx-config/schedules');
    console.log('Data:', data);

    try {
      const response = await this.client.post('/admin/vfx-config/schedules', data);
      console.log('=== API CREATE VFX SCHEDULE RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API CREATE VFX SCHEDULE ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async updateVfxSchedule(id: string, data: UpdateVfxScheduleRequest): Promise<VfxScheduleDetailResponse> {
    console.log('=== API UPDATE VFX SCHEDULE CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/vfx-config/schedules/${id}`);
    console.log('Data:', data);

    try {
      const response = await this.client.put(`/admin/vfx-config/schedules/${id}`, data);
      console.log('=== API UPDATE VFX SCHEDULE RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API UPDATE VFX SCHEDULE ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async deleteVfxSchedule(id: string): Promise<{ success: boolean; message: string }> {
    console.log('=== API DELETE VFX SCHEDULE CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/vfx-config/schedules/${id}`);

    try {
      const response = await this.client.delete(`/admin/vfx-config/schedules/${id}`);
      console.log('=== API DELETE VFX SCHEDULE RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API DELETE VFX SCHEDULE ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  // ===== TRANSACTION LIMITS & FRAUD DETECTION METHODS =====

  async getTransactionLimitsConfig(): Promise<TransactionLimitsConfigResponse> {
    console.log('=== API GET TRANSACTION LIMITS CONFIG CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/transaction-limits/config');

    try {
      const response = await this.client.get('/admin/transaction-limits/config');
      console.log('=== API GET TRANSACTION LIMITS CONFIG RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET TRANSACTION LIMITS CONFIG ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async updateTransactionLimitsConfig(data: UpdateTransactionLimitsConfigRequest): Promise<TransactionLimitsConfigResponse> {
    console.log('=== API UPDATE TRANSACTION LIMITS CONFIG CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/transaction-limits/config');
    console.log('Data:', data);

    try {
      const response = await this.client.post('/admin/transaction-limits/config', data);
      console.log('=== API UPDATE TRANSACTION LIMITS CONFIG RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API UPDATE TRANSACTION LIMITS CONFIG ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async getMessagingSettings(): Promise<MessagingPlatformSettingsResponse> {
    const response = await this.client.get('/admin/messaging/settings');
    return response.data;
  }

  async updateMessagingSettings(data: MessagingPlatformSettings): Promise<MessagingPlatformSettingsResponse> {
    const response = await this.client.patch('/admin/messaging/settings', data);
    return response.data;
  }

  async patchAdminShopMessaging(
    shopId: string,
    messagingEnabled: boolean,
  ): Promise<AdminShopMessagingPatchResponse> {
    const response = await this.client.patch(`/admin/shops/${shopId}/messaging`, { messagingEnabled });
    return response.data;
  }

  async getFraudFlags(filters?: FraudFlagFilters): Promise<FraudFlagsResponse> {
    console.log('=== API GET FRAUD FLAGS CALL ===');
    console.log('URL:', this.client.defaults.baseURL + '/admin/transaction-limits/fraud-flags');
    console.log('Filters:', filters);

    try {
      const response = await this.client.get('/admin/transaction-limits/fraud-flags', { params: filters });
      console.log('=== API GET FRAUD FLAGS RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API GET FRAUD FLAGS ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  async resolveFraudFlag(flagId: string, data: ResolveFraudFlagRequest): Promise<ResolveFraudFlagResponse> {
    console.log('=== API RESOLVE FRAUD FLAG CALL ===');
    console.log('URL:', this.client.defaults.baseURL + `/admin/transaction-limits/fraud-flags/${flagId}/resolve`);
    console.log('Data:', data);

    try {
      const response = await this.client.post(`/admin/transaction-limits/fraud-flags/${flagId}/resolve`, data);
      console.log('=== API RESOLVE FRAUD FLAG RESPONSE ===');
      console.log('Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('=== API RESOLVE FRAUD FLAG ERROR ===');
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      console.log('Error message:', error.message);
      return Promise.reject(error);
    }
  }

  // ===== SIGNUP BONUS (KIOSK-317) METHODS =====

  async getSignupBonusGrants(filters?: SignupBonusGrantsFilters): Promise<SignupBonusGrantsResponse> {
    try {
      const response = await this.client.get('/admin/transaction-limits/signup-bonus/grants', { params: filters });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getSignupBonusKpi(): Promise<SignupBonusKpiResponse> {
    try {
      const response = await this.client.get('/admin/transaction-limits/signup-bonus/kpi');
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  // ===== PROVIDER FEE CONFIG METHODS =====

  async getProviderFeeConfigs(): Promise<ProviderFeeConfigsResponse> {
    try {
      const response = await this.client.get('/admin/payment/provider-fees');
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async upsertProviderFeeConfigs(data: UpsertProviderFeeConfigsRequest): Promise<ProviderFeeConfigsResponse> {
    try {
      const response = await this.client.put('/admin/payment/provider-fees', data);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  /**
   * Public payment catalog endpoint (used by client to show coin/network lists).
   * GET /payment/supported-methods?provider=...&operation=deposit|withdrawal
   */
  async getSupportedMethods(provider: string, operation: string, options?: { includeDisabled?: boolean }) {
    try {
      const response = await this.client.get('/payment/supported-methods', {
        params: {
          provider,
          operation,
          ...(options?.includeDisabled ? { includeDisabled: 'true' } : {}),
        },
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }
  // ===== MANUAL GAME DEPOSIT METHODS =====

  async getManualGameDeposits(filters: ManualGameDepositFilters): Promise<ManualGameDepositsResponse> {
    try {
      const response = await this.client.get('/admin/manual-game-deposits', { params: filters });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getManualGameDepositDetail(transactionId: string): Promise<ManualGameDepositDetailResponse> {
    try {
      const response = await this.client.get(`/admin/manual-game-deposits/${transactionId}`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async confirmManualGameDeposit(transactionId: string, data?: ConfirmManualDepositRequest): Promise<ConfirmManualDepositResponse> {
    try {
      const response = await this.client.post(`/admin/manual-game-deposits/${transactionId}/confirm`, data || {});
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async rejectManualGameDeposit(transactionId: string, data: RejectManualDepositRequest): Promise<RejectManualDepositResponse> {
    try {
      const response = await this.client.post(`/admin/manual-game-deposits/${transactionId}/reject`, data);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async updateManualGameDepositAccount(transactionId: string, data: UpdateGameAccountRequest): Promise<UpdateGameAccountResponse> {
    try {
      const response = await this.client.put(`/admin/manual-game-deposits/${transactionId}/account`, data);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  // ===== ABNORMAL WITHDRAWAL PROTECTION METHODS =====

  async getWithdrawalProfitAnalysis(transactionId: string): Promise<ProfitAnalysisResponse> {
    try {
      const response = await this.client.get(`/payment/admin/withdrawals/${transactionId}/profit-analysis`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async adjustUserBalance(userId: string, data: AdjustBalanceRequest): Promise<AdjustBalanceResponse> {
    try {
      const response = await this.client.post(`/payment/admin/users/${userId}/adjust-balance`, data);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getPlayerAutoRefundSummary(userId: string): Promise<PlayerAutoRefundSummaryResponse> {
    const response = await this.client.get(`/payment/admin/users/${userId}/auto-refund-summary`);
    return response.data;
  }

  async clearPlayerAutoRefundWarning(
    userId: string,
    data: ClearAutoRefundWarningRequest = {}
  ): Promise<ClearAutoRefundWarningResponse> {
    const response = await this.client.post(
      `/payment/admin/users/${userId}/clear-auto-refund-warning`,
      data
    );
    return response.data;
  }

  // ─── Domain Management ────────────────────────────────────────────────────

  async getDomains(): Promise<DomainsResponse> {
    try {
      const response = await this.client.get('/admin/domains');
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async createDomain(data: CreateDomainRequest): Promise<DomainResponse> {
    try {
      const response = await this.client.post('/admin/domains', data);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async updateDomain(id: string, data: UpdateDomainRequest): Promise<DomainResponse> {
    try {
      const response = await this.client.patch(`/admin/domains/${id}`, data);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async deleteDomain(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.client.delete(`/admin/domains/${id}`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getDomainGameMappings(domainId: string): Promise<DomainGameMappingsResponse> {
    try {
      const response = await this.client.get(`/admin/domains/${domainId}/game-mappings`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async replaceDomainGameMappings(domainId: string, game_ids: string[]): Promise<DomainGameMappingsResponse> {
    try {
      const response = await this.client.put(`/admin/domains/${domainId}/game-mappings`, { game_ids });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  // ─── Kiosk Health ─────────────────────────────────────────────────────────

  async getKioskHealth(): Promise<KioskHealthResponse> {
    try {
      const response = await this.client.get('/admin/kiosk/health');
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  // ─── Domain Analytics ─────────────────────────────────────────────────────

  async getDomainAnalytics(domainId: string): Promise<DomainAnalyticsResponse> {
    try {
      const response = await this.client.get(`/admin/domains/${domainId}/analytics`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  // ─── CDN Agent Management ─────────────────────────────────────────────────

  async getCdnAgents(): Promise<AgentsListResponse> {
    try {
      const response = await this.client.get<AgentsListResponse>('/admin/agents');
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async createCdnAgent(data: CreateCdnAgentRequest): Promise<CreateCdnAgentResponse> {
    try {
      const response = await this.client.post<CreateCdnAgentResponse>('/admin/agents', {
        name: data.name,
        username: data.username,
        email: data.email,
        phone: data.phone,
        password: data.password,
        super_agent_id: data.super_agent_id,
        cost_rate: data.cost_rate ?? 0,
        can_create_shop_popup: data.can_create_shop_popup ?? false,
        can_manage_shop_popup_permission: data.can_manage_shop_popup_permission ?? false,
        default_shop_can_create_player_popup: data.default_shop_can_create_player_popup ?? false,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getSuperAgents(): Promise<SuperAgentsListResponse> {
    try {
      const response = await this.client.get<SuperAgentsListResponse>('/admin/super-agents');
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async createSuperAgent(data: CreateSuperAgentRequest): Promise<CreateSuperAgentResponse> {
    try {
      const response = await this.client.post<CreateSuperAgentResponse>('/admin/super-agents', {
        name: data.name,
        username: data.username,
        ...(data.email ? { email: data.email } : {}),
        ...(data.phone ? { phone: data.phone } : {}),
        password: data.password,
        cost_rate: data.cost_rate ?? 0,
        can_delegate_popups: data.can_delegate_popups ?? false,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getSuperAgentDetail(superAgentId: string): Promise<SuperAgentDetailResponse> {
    try {
      const response = await this.client.get<SuperAgentDetailResponse>(`/admin/super-agents/${superAgentId}`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getSuperAgentWalletTransactions(
    superAgentId: string,
    params: { page?: number; limit?: number },
  ): Promise<SuperAgentWalletTransactionsResponse> {
    try {
      const response = await this.client.get<SuperAgentWalletTransactionsResponse>(
        `/admin/super-agents/${superAgentId}/wallet-transactions`,
        { params },
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getSuperAgentGameMappings(
    superAgentId: string,
  ): Promise<{ success: boolean; data?: { game_ids: string[] } }> {
    try {
      const response = await this.client.get<{ success: boolean; data?: { game_ids: string[] } }>(
        `/admin/super-agents/${superAgentId}/game-mappings`,
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async putSuperAgentGameMappings(
    superAgentId: string,
    gameIds: string[],
  ): Promise<{ success: boolean; data?: { superAgentId: string; gameIds: string[] } }> {
    try {
      const response = await this.client.put(`/admin/super-agents/${superAgentId}/game-mappings`, {
        game_ids: gameIds,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async postAdminSuperAgentCashFunding(
    superAgentId: string,
    body: AdminAgentCashFundingRequest,
  ): Promise<AdminAgentCashFundingResponse> {
    try {
      const response = await this.client.post<AdminAgentCashFundingResponse>(
        `/admin/super-agents/${superAgentId}/cash-funding`,
        {
          credits: body.credits ?? body.amount,
          amount: body.amount ?? body.credits,
          cash_received: body.cash_received ?? body.cashReceived,
          payment_method: body.payment_method ?? body.paymentMethod,
          received_at: body.received_at ?? body.receivedAt,
          external_reference: body.external_reference ?? body.externalReference,
          note: body.note,
        },
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async postAdminSuperAgentCashWithdrawal(
    superAgentId: string,
    body: AdminAgentCashFundingRequest,
  ): Promise<AdminAgentCashFundingResponse> {
    try {
      const response = await this.client.post<AdminAgentCashFundingResponse>(
        `/admin/super-agents/${superAgentId}/cash-withdrawal`,
        {
          amount: body.amount ?? body.credits,
          note: body.note,
        },
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminCashTransactions(params: {
    page?: number;
    limit?: number;
    flow?: string;
    status?: string;
    payment_method?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    buyer_id?: string;
    buyer_type?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ success: boolean; data: import('@kioskgaming/ui').CashTransactionListResponse }> {
    try {
      const response = await this.client.get('/admin/cash-transactions', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminCashTransactionDetail(
    id: string,
    createdAt?: string,
  ): Promise<{
    success: boolean;
    data: {
      transaction: import('@kioskgaming/ui').CashTransactionRow & Record<string, unknown>;
      events: Array<Record<string, unknown>>;
      creditTransaction: Record<string, unknown> | null;
      performedBy: Record<string, unknown> | null;
    };
  }> {
    try {
      const response = await this.client.get(`/admin/cash-transactions/${id}`, {
        params: createdAt ? { created_at: createdAt } : undefined,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async exportAdminCashTransactions(params: {
    flow?: string;
    status?: string;
    payment_method?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
  }): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await this.client.get<Blob>('/admin/cash-transactions/export', {
        params,
        responseType: 'blob',
      });
      const disposition = String(response.headers['content-disposition'] || '');
      const match = /filename="([^"]+)"/i.exec(disposition);
      const filename = match?.[1] || `cash-transactions-export.csv`;
      return { blob: response.data, filename };
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchSuperAgent(
    superAgentId: string,
    body: {
      name?: string;
      cost_rate?: number;
      can_delegate_popups?: boolean;
      config_level?: 'shop' | 'agent' | 'super_agent';
      max_portal_withdrawal_per_transaction?: number | null;
      max_portal_withdrawal_per_day?: number | null;
      withdrawal_auto_approve_threshold?: number | null;
    },
  ): Promise<{ success: boolean; data: unknown }> {
    try {
      const response = await this.client.patch(`/admin/super-agents/${superAgentId}`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchSuperAgentRates(
    superAgentId: string,
    cost_rate: number,
  ): Promise<{ success: boolean; data: unknown }> {
    return this.patchSuperAgent(superAgentId, { cost_rate });
  }

  async patchSuperAgentPassword(
    superAgentId: string,
    body: { password: string },
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.client.patch<{ success: boolean; message?: string }>(
        `/admin/super-agents/${superAgentId}/password`,
        { password: body.password },
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminSuperAgentContact(
    superAgentId: string,
    body: { email?: string; phone?: string; reason: string; confirmRisk?: boolean },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/super-agents/${superAgentId}/contact`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminAgentContact(
    agentId: string,
    body: { email?: string; phone?: string; reason: string; confirmRisk?: boolean },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/contact`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminShopContact(
    agentId: string,
    shopId: string,
    body: { email?: string; phone?: string; reason: string; confirmRisk?: boolean },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/shops/${shopId}/contact`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminUserContact(
    userId: string,
    body: { email?: string; phone?: string; reason: string; confirmRisk?: boolean },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/users/${userId}/contact`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async postAdminUserCredentialsReissue(
    userId: string,
    body: { reason: string },
  ): Promise<{ success: boolean; message?: string; data?: { username?: string; tempPassword?: string } }> {
    try {
      const response = await this.client.post(`/admin/users/${userId}/credentials/reissue`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminUserGameAccounts(userId: string): Promise<{ success: boolean; data?: unknown[]; message?: string }> {
    try {
      const response = await this.client.get(`/admin/users/${userId}/game-accounts`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async postAdminUserGameAccountsCredentialsReset(
    userId: string,
    body: { gameProviderIds: string[]; mode?: 'password' | 'username_and_password'; reason?: string },
  ): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await this.client.post(`/admin/users/${userId}/game-accounts/credentials/reset`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminUserPassword(
    userId: string,
    body: { password: string; reason: string; mustChangePassword?: boolean },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/users/${userId}/password`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminUserUsername(
    userId: string,
    body: { username: string; reason: string },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/users/${userId}/username`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminAgentUsername(
    agentId: string,
    body: { username: string; reason: string },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/username`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminSuperAgentUsername(
    superAgentId: string,
    body: { username: string; reason: string },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/super-agents/${superAgentId}/username`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminShopPassword(
    agentId: string,
    shopId: string,
    body: { password: string; reason: string },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/shops/${shopId}/password`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminShopUsername(
    agentId: string,
    shopId: string,
    body: { username: string; reason: string },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/shops/${shopId}/username`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async postAdminCashierResetPassword(
    cashierId: string,
    body: { password: string; reason: string },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.post(`/admin/cashiers/${cashierId}/reset-password`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminCashierUsername(
    cashierId: string,
    body: { username: string; reason: string },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/cashiers/${cashierId}/username`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminUsername(
    adminId: string,
    body: { username: string; reason: string },
  ): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const response = await this.client.patch(`/admin/manage/${adminId}/username`, body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async suspendAdminSuperAgent(superAgentId: string): Promise<{ success: boolean; data: unknown }> {
    try {
      const response = await this.client.patch(`/admin/super-agents/${superAgentId}/suspend`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async unsuspendAdminSuperAgent(superAgentId: string): Promise<{ success: boolean; data: unknown }> {
    try {
      const response = await this.client.patch(`/admin/super-agents/${superAgentId}/unsuspend`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async terminateAdminSuperAgent(superAgentId: string): Promise<{ success: boolean; data: unknown }> {
    try {
      const response = await this.client.patch(`/admin/super-agents/${superAgentId}/terminate`, { confirm: true });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async assignAgentToSuperAgent(
    superAgentId: string,
    agentId: string,
  ): Promise<{ success: boolean; data: CdnAgent }> {
    try {
      const response = await this.client.patch<{ success: boolean; data: CdnAgent }>(
        `/admin/super-agents/${superAgentId}/agents/${agentId}/assign`,
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminAgentPopupPermissions(
    agentId: string,
    body: {
      can_create_shop_popup?: boolean;
      can_manage_shop_popup_permission?: boolean;
      default_shop_can_create_player_popup?: boolean;
    },
  ): Promise<{ success: boolean; data: CdnAgent }> {
    try {
      const response = await this.client.patch<{ success: boolean; data: CdnAgent }>(
        `/admin/agents/${agentId}/popup-permissions`,
        body,
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminShopPopupPermissions(
    agentId: string,
    shopId: string,
    body: { can_create_player_popup: boolean },
  ): Promise<{ success: boolean; data: CdnShop }> {
    try {
      const response = await this.client.patch<{ success: boolean; data: CdnShop }>(
        `/admin/agents/${agentId}/shops/${shopId}/popup-permissions`,
        body,
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async suspendCdnAgent(agentId: string): Promise<{ success: boolean; data: CdnAgent }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/suspend`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async unsuspendCdnAgent(agentId: string): Promise<{ success: boolean; data: CdnAgent }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/unsuspend`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async terminateCdnAgent(agentId: string): Promise<{ success: boolean; data: unknown }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/terminate`, { confirm: true });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAgentTerminatePreview(agentId: string): Promise<AgentTerminatePreviewResponse> {
    try {
      const response = await this.client.get<AgentTerminatePreviewResponse>(
        `/admin/agents/${agentId}/terminate/preview`,
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminAgentDetail(agentId: string): Promise<AdminAgentDetailResponse> {
    try {
      const response = await this.client.get<AdminAgentDetailResponse>(`/admin/agents/${agentId}`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminAgentPassword(
    agentId: string,
    body: { password: string },
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.client.patch<{ success: boolean; message?: string }>(
        `/admin/agents/${agentId}/password`,
        { password: body.password },
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminAgentRates(
    agentId: string,
    cost_rate: number,
  ): Promise<{ success: boolean; data: CdnAgent }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/rates`, {
        cost_rate,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminAgentPlayerWithdrawalAutoApprove(
    agentId: string,
    withdrawal_auto_approve_threshold: number | null,
  ): Promise<{ success: boolean; data: CdnAgent }> {
    try {
      const response = await this.client.patch(
        `/admin/agents/${agentId}/player-withdrawal-auto-approve-threshold`,
        { withdrawal_auto_approve_threshold },
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminShopPlayerWithdrawalAutoApprove(
    agentId: string,
    shopId: string,
    withdrawal_auto_approve_threshold: number | null,
  ): Promise<{ success: boolean; data: CdnShop }> {
    try {
      const response = await this.client.patch(
        `/admin/agents/${agentId}/shops/${shopId}/player-withdrawal-auto-approve-threshold`,
        { withdrawal_auto_approve_threshold },
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async createAdminAgentShop(agentId: string, data: CreateCdnShopRequest): Promise<CreateCdnShopResponse> {
    try {
      const response = await this.client.post<CreateCdnShopResponse>(`/admin/agents/${agentId}/shops`, {
        name: data.name,
        ...(data.username ? { username: data.username } : {}),
        email: data.email,
        phone: data.phone,
        password: data.password,
        cost_rate: data.cost_rate ?? 0,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminAgentShopDetail(agentId: string, shopId: string): Promise<AdminShopDetailResponse> {
    try {
      const response = await this.client.get<AdminShopDetailResponse>(
        `/admin/agents/${agentId}/shops/${shopId}`,
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminShopDetailByShopId(shopId: string): Promise<AdminShopDetailResponse> {
    try {
      const response = await this.client.get<AdminShopDetailResponse>(`/admin/shops/${shopId}`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  // ===== Cashier Management (Admin) =====
  async getAdminCashiers(params?: {
    page?: number;
    limit?: number;
    agent_id?: string;
    shop_id?: string;
    status?: string;
    q?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }): Promise<AdminCashiersListResponse> {
    try {
      const response = await this.client.get<AdminCashiersListResponse>('/admin/cashiers', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async createAdminCashier(body: AdminCreateCashierRequest): Promise<AdminCreateCashierResponse> {
    try {
      const response = await this.client.post<AdminCreateCashierResponse>('/admin/cashiers', body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async suspendAdminCashier(cashierId: string): Promise<{ success: boolean }> {
    try {
      const response = await this.client.patch<{ success: boolean }>(`/admin/cashiers/${cashierId}/suspend`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async unsuspendAdminCashier(cashierId: string): Promise<{ success: boolean }> {
    try {
      const response = await this.client.patch<{ success: boolean }>(`/admin/cashiers/${cashierId}/unsuspend`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async terminateAdminCashier(cashierId: string): Promise<{ success: boolean }> {
    try {
      const response = await this.client.patch<{ success: boolean }>(`/admin/cashiers/${cashierId}/terminate`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminCashierReport(
    cashierId: string,
    params?: { period?: string; from?: string; to?: string }
  ): Promise<AdminCashierReportResponse> {
    try {
      const response = await this.client.get<AdminCashierReportResponse>(`/admin/cashiers/${cashierId}/report`, {
        params,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminCashierShifts(
    cashierId: string,
    params?: { from?: string; to?: string }
  ): Promise<AdminCashierShiftHistoryResponse> {
    try {
      const response = await this.client.get<AdminCashierShiftHistoryResponse>(`/admin/cashiers/${cashierId}/shifts`, {
        params,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminFraudIndicators(params?: {
    period?: string;
    from?: string;
    to?: string;
    highRedemptionThreshold?: number;
    discrepancyThreshold?: number;
    shop_id?: string;
    agent_id?: string;
  }): Promise<AdminFraudIndicatorsResponse> {
    try {
      const response = await this.client.get<AdminFraudIndicatorsResponse>('/admin/cashiers/fraud-indicators', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async patchAdminShopRates(
    agentId: string,
    shopId: string,
    cost_rate: number,
  ): Promise<{ success: boolean; data: CdnShop }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/shops/${shopId}/rates`, {
        cost_rate,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async postAdminShopCreditAdjust(
    agentId: string,
    shopId: string,
    body: AdminShopCreditAdjustRequest,
  ): Promise<AdminShopCreditAdjustResponse> {
    try {
      const response = await this.client.post<AdminShopCreditAdjustResponse>(
        `/admin/agents/${agentId}/shops/${shopId}/credit-adjust`,
        body,
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async postAdminFinanceCorrection(body: AdminFinanceCorrectionRequest): Promise<AdminFinanceCorrectionResponse> {
    try {
      const response = await this.client.post<AdminFinanceCorrectionResponse>('/admin/finance-corrections', body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async suspendAdminShop(agentId: string, shopId: string): Promise<{ success: boolean; data: CdnShop }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/shops/${shopId}/suspend`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async unsuspendAdminShop(agentId: string, shopId: string): Promise<{ success: boolean; data: CdnShop }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/shops/${shopId}/unsuspend`);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async terminateAdminShop(agentId: string, shopId: string): Promise<{ success: boolean; data: unknown }> {
    try {
      const response = await this.client.patch(`/admin/agents/${agentId}/shops/${shopId}/terminate`, {
        confirm: true,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminAgentPaymentTransactions(
    agentId: string,
    params: { page?: number; limit?: number; status?: string; type?: string; provider?: string },
  ): Promise<AdminAgentPaymentTransactionsResponse> {
    try {
      const response = await this.client.get<AdminAgentPaymentTransactionsResponse>(
        `/admin/agents/${agentId}/payment-transactions`,
        { params },
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminAgentWalletTransactions(
    agentId: string,
    params: { page?: number; limit?: number; type?: string; shop_id?: string },
  ): Promise<AdminAgentWalletTransactionsResponse> {
    try {
      const response = await this.client.get<AdminAgentWalletTransactionsResponse>(
        `/admin/agents/${agentId}/wallet-transactions`,
        { params },
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getCostReport(params?: {
    agent_id?: string;
    from?: string;
    to?: string;
    event_type?: 'shop_direct_topup' | 'user_direct_topup';
    page?: number;
    limit?: number;
  }): Promise<CostReportResponse> {
    try {
      const response = await this.client.get<CostReportResponse>('/admin/reports/cost', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminBroadcasts(params?: {
    page?: number;
    limit?: number;
    /** Server-side filter: player | agent | shop */
    channel?: 'player' | 'agent' | 'shop';
  }): Promise<AdminBroadcastsListResponse> {
    try {
      const response = await this.client.get<AdminBroadcastsListResponse>('/admin/broadcasts', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async createAdminBroadcast(body: CreateAdminBroadcastRequest): Promise<{ success: boolean; data: unknown }> {
    try {
      const response = await this.client.post('/admin/broadcasts', {
        title: body.title,
        body: body.body,
        audience: body.audience,
        targetIds: body.targetIds ?? [],
        expiresAt: body.expiresAt ?? null,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async enqueueCredentialSmsBatch(body: {
    targetUserIds: string[];
    messageTemplate: string;
  }): Promise<{ success: boolean; data?: { queued: number; requested: number; status: string }; message?: string }> {
    try {
      const response = await this.client.post('/admin/broadcasts/credential-sms/batch', body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminBroadcastShopsMeta(params?: { limit?: number }): Promise<AdminBroadcastShopsMetaResponse> {
    try {
      const response = await this.client.get<AdminBroadcastShopsMetaResponse>('/admin/broadcasts/meta/shops', {
        params,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminNotifications(
    limit?: number,
    options?: { unreadOnly?: boolean },
  ): Promise<{
    success: boolean;
    data?: { notifications: AdminNotificationApiRow[] };
    message?: string;
  }> {
    try {
      const params: Record<string, string | number> = {};
      if (limit != null) params.limit = limit;
      if (options?.unreadOnly) params.unreadOnly = '1';
      const response = await this.client.get('/admin/me/notifications', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminNotificationUnreadCount(): Promise<{
    success: boolean;
    data?: { unreadCount: number };
    message?: string;
  }> {
    try {
      const response = await this.client.get('/admin/me/notifications/unread-count');
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminNotificationsAll(params?: {
    limit?: number;
    cursor?: string;
    category?: string;
    severity?: string;
    unreadOnly?: boolean;
  }): Promise<{
    success: boolean;
    data?: {
      notifications: AdminNotificationApiRow[];
      nextCursor: string | null;
      hasMore: boolean;
    };
    message?: string;
  }> {
    try {
      const response = await this.client.get('/admin/me/notifications/all', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async ackAdminNotification(notificationId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.client.post(`/admin/me/notifications/${notificationId}/ack`, {});
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async ackAllAdminNotifications(params?: {
    category?: string;
    severity?: string;
  }): Promise<{ success: boolean; data?: { acknowledged: number }; message?: string }> {
    try {
      const response = await this.client.post('/admin/me/notifications/ack-all', params ?? {});
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async createAdminAnnouncement(body: {
    title: string;
    body: string;
    requiredPermissions?: string[];
    requiredRoles?: string[] | null;
  }): Promise<{ success: boolean; data?: { notification: AdminNotificationApiRow }; message?: string }> {
    try {
      const response = await this.client.post('/admin/notifications/announcements', body);
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  /** @deprecated Admin bell uses /admin/me/notifications — legacy AdminBroadcast feed */
  async getAdminMeBroadcasts(): Promise<{
    success: boolean;
    data?: { broadcasts: Array<{ id: string; title: string; body: string; createdAt: string; acknowledged: boolean }> };
    message?: string;
  }> {
    try {
      const response = await this.client.get('/admin/me/broadcasts');
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async ackAdminMeBroadcast(broadcastId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.client.post(`/admin/me/broadcasts/${broadcastId}/ack`, {});
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminInAppPopups(params?: {
    page?: number;
    limit?: number;
    /** Server-side filter: player | agent | shop */
    channel?: 'player' | 'agent' | 'shop';
  }): Promise<AdminBroadcastsListResponse> {
    try {
      const response = await this.client.get<AdminBroadcastsListResponse>('/admin/in-app-popups', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async createAdminInAppPopup(body: CreateAdminBroadcastRequest): Promise<{ success: boolean; data: unknown }> {
    try {
      const response = await this.client.post('/admin/in-app-popups', {
        title: body.title,
        body: body.body,
        audience: body.audience,
        targetIds: body.targetIds ?? [],
        expiresAt: body.expiresAt ?? null,
        displayMode: body.displayMode ?? 'once',
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getAdminInAppPopupShopsMeta(params?: { limit?: number }): Promise<AdminBroadcastShopsMetaResponse> {
    try {
      const response = await this.client.get<AdminBroadcastShopsMetaResponse>('/admin/in-app-popups/meta/shops', {
        params,
      });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  // ===== USD Wallets (gross/net) =====
  async getUsdWalletSummary(params: {
    ownerType: string;
    ownerId: string;
    currency?: string;
  }): Promise<UsdWalletSummaryResponse> {
    try {
      const response = await this.client.get<UsdWalletSummaryResponse>('/admin/usd-wallets/summary', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getUsdWalletLedger(params: {
    ownerType?: string;
    ownerId?: string;
    currency?: string;
    type?: string;
    status?: string;
    query?: string;
    transactionId?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<UsdWalletLedgerResponse> {
    try {
      const response = await this.client.get<UsdWalletLedgerResponse>('/admin/usd-wallets/ledger', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async exportUsdWalletLedger(params: {
    ownerType?: string;
    ownerId?: string;
    currency?: string;
    type?: string;
    status?: string;
    query?: string;
    dateFrom: string;
    dateTo: string;
  }): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await this.client.get<Blob>('/admin/usd-wallets/ledger/export', {
        params,
        responseType: 'blob',
      });
      const disposition = String(response.headers['content-disposition'] || '');
      const match = /filename="([^"]+)"/i.exec(disposition);
      const filename =
        match?.[1] || `usd-wallet-ledger_${params.dateFrom}_to_${params.dateTo}.csv`;
      return { blob: response.data, filename };
    } catch (error: any) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text) as { message?: string; error?: string };
          const msg = parsed.message || parsed.error || 'Export failed';
          return Promise.reject(new Error(typeof msg === 'string' ? msg : 'Export failed'));
        } catch {
          return Promise.reject(new Error('Export failed'));
        }
      }
      return Promise.reject(error);
    }
  }

  async getUsdWalletLedgerEntry(id: string): Promise<UsdWalletLedgerEntryResponse> {
    const response = await this.client.get<UsdWalletLedgerEntryResponse>(
      `/admin/usd-wallets/ledger/${encodeURIComponent(id)}`,
    );
    return response.data;
  }

  /** CDN `wallet_transactions` that reference this USD row (`reference_type=usd_wallet_transaction`, `reference_id`=USD row id). */
  async getUsdWalletLinkedCreditTransactions(
    usdWalletTransactionId: string,
  ): Promise<{ success: boolean; data?: { rows: Record<string, unknown>[] }; error?: string }> {
    const response = await this.client.get<{
      success: boolean;
      data?: { rows: Record<string, unknown>[] };
      error?: string;
    }>(`/admin/usd-wallets/ledger/${encodeURIComponent(usdWalletTransactionId)}/linked-credit-transactions`);
    return response.data;
  }

  async approveUsdWalletWithdrawal(id: string, body?: { note?: string | null }): Promise<{ success: boolean; data?: any; message?: string }> {
    const response = await this.client.post(`/admin/usd-wallets/withdrawals/${encodeURIComponent(id)}/approve`, {
      note: body?.note ?? null,
    });
    return response.data;
  }

  async rejectUsdWalletWithdrawal(id: string, body?: { note?: string | null }): Promise<{ success: boolean; data?: any; message?: string }> {
    const response = await this.client.post(`/admin/usd-wallets/withdrawals/${encodeURIComponent(id)}/reject`, {
      note: body?.note ?? null,
    });
    return response.data;
  }

  // ===== Credit wallets (wallets + wallet_transactions) =====
  async getCreditWalletSummary(params: { ownerType: string; ownerId: string }): Promise<CreditWalletSummaryResponse> {
    try {
      const response = await this.client.get<CreditWalletSummaryResponse>('/admin/credit-wallets/summary', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getCreditWalletLedger(params: {
    ownerType?: string;
    ownerId?: string;
    type?: string;
    period?: string;
    date_from?: string;
    date_to?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sort?: string;
    sort_dir?: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    limit?: number;
    offset?: number;
  }): Promise<CreditWalletLedgerResponse> {
    try {
      const response = await this.client.get<CreditWalletLedgerResponse>('/admin/credit-wallets/ledger', { params });
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async getCreditWalletLedgerEntry(id: string): Promise<{ item: Record<string, unknown> }> {
    try {
      const response = await this.client.get<{ item: Record<string, unknown> }>(
        `/admin/credit-wallets/ledger/${encodeURIComponent(id)}`,
      );
      return response.data;
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  async exportCreditWalletLedger(params: {
    ownerType?: string;
    ownerId?: string;
    period?: string;
    date_from?: string;
    date_to?: string;
    type?: string;
    search?: string;
  }): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await this.client.get<Blob>('/admin/credit-wallets/ledger/export', {
        params,
        responseType: 'blob',
      });
      const disposition = String(response.headers['content-disposition'] || '');
      const match = /filename="([^"]+)"/i.exec(disposition);
      const filename =
        match?.[1] ||
        `credit-transactions_${params.date_from ?? 'export'}_to_${params.date_to ?? 'export'}.csv`;
      return { blob: response.data, filename };
    } catch (error: any) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text) as { message?: string; error?: string };
          const msg = parsed.message || parsed.error || 'Export failed';
          return Promise.reject(new Error(typeof msg === 'string' ? msg : 'Export failed'));
        } catch {
          return Promise.reject(new Error('Export failed'));
        }
      }
      return Promise.reject(error);
    }
  }

  // ===== Integration API Logs (super admin) =====
  async getIntegrationGameLogs(params: Record<string, string | number | undefined>) {
    const response = await this.client.get('/admin/api-logs/game', { params });
    return response.data;
  }

  async getIntegrationGameLogDetail(id: string) {
    const response = await this.client.get(`/admin/api-logs/game/${id}`);
    return response.data;
  }

  async getIntegrationPaymentLogs(params: Record<string, string | number | undefined>) {
    const response = await this.client.get('/admin/api-logs/payment', { params });
    return response.data;
  }

  async getIntegrationPaymentLogDetail(id: string) {
    const response = await this.client.get(`/admin/api-logs/payment/${id}`);
    return response.data;
  }

  async getIntegrationWebhookLogs(params: Record<string, string | number | undefined>) {
    const response = await this.client.get('/admin/api-logs/webhooks', { params });
    return response.data;
  }

  async getIntegrationWebhookLogDetail(id: string | number) {
    const response = await this.client.get(`/admin/api-logs/webhooks/${id}`);
    return response.data;
  }

  /** KIOSK-414 — global admin audit log (system/super only) */
  async getAdminAuditLogs(params: Record<string, string | number | undefined>) {
    const response = await this.client.get('/admin/audit-logs', { params });
    return response.data;
  }
}

export const apiClient = new ApiClient();
